import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DndContext, DragEndEvent, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import {
  Home,
  Target,
  Users,
  FolderKanban,
  Calendar,
  BarChart3,
  Settings,
  Star,
  ChevronDown,
  BookOpen,
  Inbox,
  CheckSquare,
  Building2,
  TrendingUp,
  ScrollText,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useFavorites } from '@/hooks/useFavorites';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DraggableMenuItem } from './DraggableMenuItem';
import { FavoriteMenuItem } from './FavoriteMenuItem';
import { DropIndicator } from './DropIndicator';
import { TenantSwitcher } from './TenantSwitcher';

// Menu items with Monday.com style organization
const menuItems = [
  {
    title: 'Dashboard',
    url: '/',
    icon: Home,
    color: 'bg-primary',
  },
  {
    title: 'Account Planning',
    url: '/account-planning',
    icon: Target,
    color: 'bg-success',
  },
  {
    title: 'Leads',
    url: '/leads',
    icon: Users,
    color: 'bg-info',
  },
  {
    title: 'Opportunities',
    url: '/opportunities',
    icon: TrendingUp,
    color: 'bg-success',
  },
  {
    title: 'Partners',
    url: '/companies',
    icon: Building2,
    color: 'bg-emerald-500',
  },
  {
    title: 'Partner Analytics',
    url: '/partners/analytics',
    icon: BarChart3,
    color: 'bg-cyan-500',
  },
  {
    title: 'Contracts',
    url: '/contracts',
    icon: ScrollText,
    color: 'bg-amber-500',
  },
  {
    title: 'Divisions',
    url: '/divisions',
    icon: Target,
    color: 'bg-indigo-500',
  },
  {
    title: 'Contacts',
    url: '/contacts',
    icon: BookOpen,
    color: 'bg-warning',
  },
  {
    title: 'Projects',
    url: '/projects',
    icon: FolderKanban,
    color: 'bg-accent',
  },
  {
    title: 'Inbox',
    url: '/inbox',
    icon: Inbox,
    color: 'bg-purple-500',
    subItems: [
      { title: 'All Mail', url: '/inbox?view=all', icon: Inbox },
      { title: 'Unread', url: '/inbox?view=unread', icon: Inbox },
      { title: 'Starred', url: '/inbox?view=starred', icon: Star },
      { title: 'Important', url: '/inbox?view=important', icon: Star },
      { title: 'Awaiting Reply', url: '/inbox?view=awaiting_reply', icon: Inbox },
      { title: 'Snoozed', url: '/inbox?view=snoozed', icon: Inbox },
    ],
  },
  {
    title: 'Tasks',
    url: '/tasks',
    icon: CheckSquare,
    color: 'bg-blue-500',
  },
  {
    title: 'Calendar',
    url: '/calendar',
    icon: Calendar,
    color: 'bg-secondary',
  },
  {
    title: 'Reports',
    url: '/reports',
    icon: BarChart3,
    color: 'bg-destructive',
  },
  {
    title: 'Settings',
    url: '/settings',
    icon: Settings,
    color: 'bg-muted-foreground',
  },
  {
    title: 'Tenant Management',
    url: '/tenants',
    icon: Building2,
    color: 'bg-purple-600',
    superAdminOnly: true,
  },
];

export function DashboardSidebar() {
  const location = useLocation();
  const { signOut } = useAuth();
  const { isSuperAdmin } = useUserRole();
  const { favorites: userFavorites, addFavorite, removeFavorite, reorderFavorites } = useFavorites();
  const [favoritesOpen, setFavoritesOpen] = useState(true);
  const [workspaceOpen, setWorkspaceOpen] = useState(true);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  console.log('📊 DashboardSidebar - isSuperAdmin:', isSuperAdmin);

  const favoriteMenuItems = userFavorites
    .map(fav => menuItems.find(item => item.url === fav.menu_item_url))
    .filter(Boolean);

  const handleDragStart = (event: any) => {
    setActiveDragId(event.active.id);
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    
    if (!over) {
      setDropTargetIndex(null);
      return;
    }
    
    const overId = String(over.id);
    
    // Handle explicit drop zones
    if (overId.startsWith('drop-')) {
      const index = parseInt(overId.split('-')[1]);
      setDropTargetIndex(index);
    } 
    // Handle hovering over favorite items - show drop zone after the item
    else if (overId.startsWith('fav-')) {
      const hoveredUrl = overId.replace('fav-', '');
      const hoveredIndex = favoriteMenuItems.findIndex(item => item?.url === hoveredUrl);
      if (hoveredIndex !== -1) {
        // Show drop indicator AFTER the hovered item
        setDropTargetIndex(hoveredIndex + 1);
      }
    } 
    else {
      setDropTargetIndex(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);
    setDropTargetIndex(null);

    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    
    // Check if active item is from workspace or from favorites
    const isFromWorkspace = menuItems.some(item => item.url === activeId);
    const isFromFavorites = activeId.startsWith('fav-');
    
    // CASE 1: Dragging from workspace to add new favorite
    if (isFromWorkspace && !isFromFavorites) {
      const activeItem = menuItems.find(item => item.url === activeId);
      if (!activeItem) return;
      
      const isAlreadyFavorite = userFavorites.some(f => f.menu_item_url === activeItem.url);
      
      // Don't add if already a favorite or limit reached
      if (isAlreadyFavorite || userFavorites.length >= 8) return;
      
      // Calculate target index based on drop location
      let targetIndex = 0;
      
      if (overId.startsWith('drop-')) {
        targetIndex = parseInt(overId.split('-')[1]);
      } else if (overId.startsWith('fav-')) {
        // Use the dropTargetIndex we calculated in handleDragOver
        targetIndex = dropTargetIndex ?? favoriteMenuItems.length;
      }
      
      addFavorite({ url: activeItem.url, targetIndex });
    }
    
    // CASE 2: Reordering existing favorites
    else if (isFromFavorites && overId.startsWith('fav-')) {
      const activeUrl = activeId.replace('fav-', '');
      const overUrl = overId.replace('fav-', '');
      
      const oldIndex = favoriteMenuItems.findIndex(item => item?.url === activeUrl);
      const newIndex = favoriteMenuItems.findIndex(item => item?.url === overUrl);
      
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = arrayMove(favoriteMenuItems, oldIndex, newIndex);
        reorderFavorites(reordered.map(item => item!.url));
      }
    }
  };

  const handleRemoveFavorite = (url: string) => {
    removeFavorite(url);
  };

  const activeDragItem = menuItems.find(item => item.url === activeDragId);

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Sidebar className="border-r border-border bg-sidebar">
        <SidebarHeader className="border-b border-sidebar-border p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
              BC
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-sm text-sidebar-foreground">BuildCRM</h2>
              <p className="text-xs text-muted-foreground">Main Workspace</p>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent className="px-2">
          {/* Favorites Section */}
          <SidebarGroup>
            <button
              onClick={() => setFavoritesOpen(!favoritesOpen)}
              className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5" />
                <span>Favorites {userFavorites.length > 0 && `(${userFavorites.length})`}</span>
              </div>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform',
                  favoritesOpen && 'rotate-180'
                )}
              />
            </button>
            {favoritesOpen && (
              <SidebarGroupContent className="mt-1">
                {favoriteMenuItems.length === 0 ? (
                  <div className="relative">
                    <DropIndicator 
                      id="drop-0" 
                      isVisible={activeDragId !== null} 
                    />
                    <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                      Drag items from workspace to add favorites
                    </div>
                  </div>
                ) : (
                  <SortableContext
                    items={favoriteMenuItems.map(item => `fav-${item!.url}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <SidebarMenu>
                      <DropIndicator id="drop-0" isVisible={activeDragId !== null && dropTargetIndex === 0} />
                      {favoriteMenuItems.map((item, index) => {
                        if (!item) return null;
                        return (
                          <div key={item.url}>
                            <FavoriteMenuItem
                              id={`fav-${item.url}`}
                              url={item.url}
                              title={item.title}
                              icon={item.icon}
                              color={item.color}
                              onRemove={() => handleRemoveFavorite(item.url)}
                            />
                            <DropIndicator
                              id={`drop-${index + 1}`}
                              isVisible={activeDragId !== null && dropTargetIndex === index + 1}
                            />
                          </div>
                        );
                      })}
                    </SidebarMenu>
                  </SortableContext>
                )}
              </SidebarGroupContent>
            )}
          </SidebarGroup>

          {/* Main Navigation */}
          <SidebarGroup className="mt-4">
            <button
              onClick={() => setWorkspaceOpen(!workspaceOpen)}
              className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>MAIN WORKSPACE</span>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform',
                  workspaceOpen && 'rotate-180'
                )}
              />
            </button>
            {workspaceOpen && (
              <SidebarGroupContent className="mt-1">
                <SidebarMenu>
                  {menuItems.map((item) => {
                    // Skip super admin only items if user is not super admin
                    if ((item as any).superAdminOnly && !isSuperAdmin) {
                      return null;
                    }
                    const isFavorited = userFavorites.some(f => f.menu_item_url === item.url);
                    return (
                      <DraggableMenuItem
                        key={item.url}
                        id={item.url}
                        url={item.url}
                        title={item.title}
                        icon={item.icon}
                        color={item.color}
                        isFavorited={isFavorited}
                      />
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="border-t border-sidebar-border p-3 space-y-3">
          <TenantSwitcher />
          <Button
            variant="ghost"
            onClick={signOut}
            className="w-full justify-start text-sm hover:bg-sidebar-accent"
          >
            Sign Out
          </Button>
        </SidebarFooter>
      </Sidebar>

      <DragOverlay>
        {activeDragId && activeDragItem && (
          <div className="flex items-center gap-3 px-3 py-2 bg-sidebar-accent rounded-lg shadow-lg">
            <div className={cn('flex h-7 w-7 items-center justify-center rounded-md text-white', activeDragItem.color)}>
              <activeDragItem.icon className="h-4 w-4" />
            </div>
            <span className="text-sm font-medium">{activeDragItem.title}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
